import React, { useState, useEffect, useMemo, useRef } from 'react'
import InfoButton from './InfoButton'
import { Ban, ChevronDown, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import ScrollNavigation from './ScrollNavigation'
import { IInfoDetails, StockStatus } from '../context/ProductDataStore'
import EngravingInput from './EngravingInput'
import { formatPriceNumber } from '@/utils/priceFormatting'

export interface IAddonItem {
	id: string
	imageUrl: string
	title: string
	price: number
	currency: string
	triggerInput?: boolean
	inputPlaceholder?: string
	inputMaxLength?: number
	inputPattern?: string
	inputClassName?: string
	inputValidation?: (value: string) => boolean
	triggersSpecialSelect?: boolean
	originalPrice?: number
	group?: string
	isFree?: boolean
	stockStatus?: StockStatus
	stockMessage?: string
	remainingStock?: number | null
	availableForSale?: boolean
	sharedPoolBlocked?: boolean
	sharedPoolMessage?: string
}

type AddonProps = {
	title: string
	description: string | null
	items: IAddonItem[]
	activeId: string | null
	onSelect: (id: string | null) => void
	onSpecialSelect?: () => void
	onInputChange?: (id: string, value: string) => void
	required?: boolean
	className?: string
	info?: IInfoDetails | null
	designatedCollapsibleGroups?: string[]
	showInlineGroups?: string[]
	onMoreButtonClick?: (items: IAddonItem[]) => void
	badge?: React.ReactNode
	primaryTextColor?: string
	secondaryTextColor?: string
	priceColor?: string
	onBlockedSelect?: (item: IAddonItem) => void
}

const Addon: React.FC<AddonProps> = ({
	title,
	description,
	items,
	activeId,
	onSelect,
	onSpecialSelect = null,
	onInputChange,
	required = false,
	badge,
	className = '',
	info,
	designatedCollapsibleGroups = [],
	showInlineGroups = [],
	onMoreButtonClick,
	primaryTextColor,
	secondaryTextColor,
	priceColor,
	onBlockedSelect
}) => {
	const [inputVisible, setInputVisible] = useState<boolean>(false)
	const [inputValue, setInputValue] = useState<string>('')
	const [addonItems, setAddonItems] = useState<IAddonItem[]>([])
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [canScrollPrevious, setCanScrollPrevious] = useState(false)
	const [canScrollNext, setCanScrollNext] = useState(false)
	const [needsScroll, setNeedsScroll] = useState(false)
	const defaultItem: IAddonItem = {
		id: '',
		imageUrl: '',
		title: '',
		price: 0,
		currency: '',
		triggerInput: false
	}

	const designatedCollapsibleGroupsSerialized = useMemo(() => JSON.stringify(designatedCollapsibleGroups), [designatedCollapsibleGroups]);

	useEffect(() => {
		const initialExpandedState: Record<string, boolean> = {};
		designatedCollapsibleGroups.forEach(group => {
			initialExpandedState[group] = group !== "Original";
		});
		setExpandedGroups(initialExpandedState);
	}, [designatedCollapsibleGroupsSerialized]);

	const { ungroupedItems, groupedItems, moreButtonItems } = useMemo(() => {
		const ugItems: IAddonItem[] = [];
		const gItems: Record<string, IAddonItem[]> = {};
		const moreItems: IAddonItem[] = [];

		items.forEach(item => {
			if (!item.group || showInlineGroups.includes(item.group)) {
				ugItems.push(item);
			} else if (item.group && designatedCollapsibleGroups.includes(item.group)) {
				if (!gItems[item.group]) {
					gItems[item.group] = [];
				}
				gItems[item.group].push(item);
			} else {
				moreItems.push(item);
			}
		});

		// Handle promotion of items from "More" to main display
		if (activeId) {
			// Check if the selected item is in the "More" items
			const selectedMoreItemIndex = moreItems.findIndex(item => item.id === activeId);
			if (selectedMoreItemIndex !== -1) {
				// Found selected item in "More" - promote it to ungrouped items
				const selectedItem = moreItems[selectedMoreItemIndex];
				moreItems.splice(selectedMoreItemIndex, 1); // Remove from More
				ugItems.unshift(selectedItem); // Add to the beginning of ungrouped
			} else {
				// Selected item is not in "More", check if we need to demote a previously promoted item
				const selectedInUngrouped = ugItems.find(item => item.id === activeId);
				if (selectedInUngrouped) {
					// Find items in ungrouped that originally belonged to "More" (not in showInlineGroups)
					const originallyMoreItems = ugItems.filter(item =>
						item.group &&
						!showInlineGroups.includes(item.group) &&
						!designatedCollapsibleGroups.includes(item.group) &&
						item.id !== activeId // Don't demote the currently selected item
					);

					// Move originally "More" items back to More (except the currently selected one)
					originallyMoreItems.forEach(item => {
						const itemIndex = ugItems.findIndex(ugItem => ugItem.id === item.id);
						if (itemIndex !== -1) {
							ugItems.splice(itemIndex, 1);
							moreItems.push(item);
						}
					});
				}
			}
		}

		return { ungroupedItems: ugItems, groupedItems: gItems, moreButtonItems: moreItems };
	}, [items, designatedCollapsibleGroups, showInlineGroups, activeId]);

	const renderableElements = useMemo(() => {
		const elements: (IAddonItem | { type: 'groupHeader'; name: string } | { type: 'moreButton'; items: IAddonItem[] })[] = [];
		if (!required) {
			elements.push(defaultItem);
		}
		elements.push(...ungroupedItems);

		Object.keys(groupedItems).sort().forEach(groupName => {
			elements.push({ type: 'groupHeader', name: groupName });
			if (expandedGroups[groupName]) {
				elements.push(...groupedItems[groupName]);
			}
		});

		if (moreButtonItems.length > 0) {
			elements.push({ type: 'moreButton', items: moreButtonItems });
		}

		return elements;
	}, [required, ungroupedItems, groupedItems, moreButtonItems, expandedGroups, defaultItem]);

	useEffect(() => {
		if (activeId && onInputChange) {
			onInputChange(activeId, inputValue)
		}
	}, [inputValue, activeId, onInputChange])

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current
		if (!scrollContainer) return

		const updateScrollState = () => {
			const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0)
			setNeedsScroll(maxScrollLeft > 1)
			setCanScrollPrevious(scrollContainer.scrollLeft > 1)
			setCanScrollNext(scrollContainer.scrollLeft < maxScrollLeft - 1)
		}

		scrollContainer.addEventListener('scroll', updateScrollState, { passive: true })
		window.addEventListener('resize', updateScrollState)
		const resizeObserver = typeof ResizeObserver === 'undefined'
			? null
			: new ResizeObserver(updateScrollState)
		resizeObserver?.observe(scrollContainer)
		updateScrollState()

		return () => {
			scrollContainer.removeEventListener('scroll', updateScrollState)
			window.removeEventListener('resize', updateScrollState)
			resizeObserver?.disconnect()
		}
	}, [renderableElements.length])

	const handleScrollNavigation = (direction: 'prev' | 'next') => {
		const scrollContainer = scrollContainerRef.current
		if (!scrollContainer) return

		const firstItem = scrollContainer.querySelector<HTMLElement>('.item')
		const styles = window.getComputedStyle(scrollContainer)
		const gap = parseFloat(styles.columnGap || styles.gap || '0')
		const scrollStep = firstItem ? firstItem.offsetWidth + gap : scrollContainer.clientWidth * 0.8
		scrollContainer.scrollBy({
			left: direction === 'next' ? scrollStep : -scrollStep,
			behavior: 'smooth',
		})
	}

	const handleHeaderClick = (groupName: string) => {
		setExpandedGroups(prev => ({
			...prev,
			[groupName]: !prev[groupName]
		}));
	};

	const renderInput = (item: IAddonItem | undefined) => {
		if (!item?.triggerInput || !item.id) return null

		return (
			<EngravingInput
				value={inputValue || ''}
				onChange={(value) => {
					setInputValue(value)
					if (onInputChange) onInputChange(item.id, value)
				}}
				maxLength={item.inputMaxLength}
				className={item.inputClassName}
				placeholder={item.inputPlaceholder}
			/>
		)
	}

	const isPurchasableStatus = (status?: StockStatus, availableForSale?: boolean) => {
		if (availableForSale === false) {
			return false
		}
		return !status || status === 'IN_STOCK' || status === 'LOW_STOCK' || status === 'PREORDER'
	}

	const formatStockMessage = (message?: string, remaining?: number | null): string | null => {
		if (!message) return null
		if (remaining === null || remaining === undefined) return message
		return message.replace('{digit_count}', String(remaining))
	}

	const getStatusLabel = (status?: StockStatus): string | null => {
		switch (status) {
			case 'LOW_STOCK':
				return 'Low Stock'
			case 'PREORDER':
				return 'Preorder'
			case 'OUT_OF_STOCK':
				return 'Sold Out'
			case 'COMING_SOON':
				return 'Coming Soon'
			case 'BACK_SOON':
				return 'Back Soon'
			case 'NOT_YET_AVAILABLE':
				return 'Coming Soon'
			case 'NO_LONGER_AVAILABLE':
				return 'No Longer Available'
			case 'INSUFFICIENT_STOCK':
				return 'Insufficient Stock'
			case 'EXCEEDS_MAX_PER_ORDER':
				return 'Limit Reached'
			default:
				return null
		}
	}

	const renderPriceLabel = (item: IAddonItem) => {
		if (item.price === 0) {
			return null
		}

		const isNegative = item.price < 0
		const sign = isNegative ? '-' : '+'
		const textColor = priceColor || 'text-orange'

		return (
			<h5 className={`${textColor} text-sm m-0`}>
				{sign}{item.currency}{formatPriceNumber(Math.abs(item.price))}
			</h5>
		)
	}

	return (
		<div className="addon flex flex-col gap-0">
			<div className="addon-meta flex flex-col gap-1">
				<div className="flex flex-row gap-4 justify-between items-center">
					<div className="min-w-0">
						<h2 className={`leading-none m-0 flex flex-row gap-3 items-center ${primaryTextColor || 'text-black'}`}>{title}{badge}</h2>
					</div>
					<div className="flex shrink-0 items-center gap-3">
						{needsScroll && (
							<ScrollNavigation
								size="small"
								onPrevious={() => handleScrollNavigation('prev')}
								onNext={() => handleScrollNavigation('next')}
								canScrollPrevious={canScrollPrevious}
								canScrollNext={canScrollNext}
							/>
						)}
						{info && <InfoButton title={info.title} text={info.text} />}
					</div>
				</div>
				<p className={`leading-none ${!description && 'hidden'} ${secondaryTextColor || 'text-gray-600'}`}>{description}</p>
			</div>

			<div ref={scrollContainerRef} className="flex overflow-x-auto pt-4 pb-0 px-4 -mx-4 items-start no-scrollbar gap-1 relative">
				{renderableElements.map((element) => {
					if ('type' in element && element.type === 'groupHeader') {
						const isExpanded = expandedGroups[element.name];
						return (
							<div className="item flex flex-col gap-1 m-1 grow-0 max-w-[120px] transform-gpu" key={`group-${element.name}`}>
								<button
									onClick={() => handleHeaderClick(element.name)}
									className={`group group-header w-[120px] aspect-[4/4] transition-all ease-in-out border border-[3px] rounded-xl 
										overflow-hidden outline outline-[3px] md:hover:outline-orange hover:duration-75 md:hover:scale-105 bg-grey 
										flex flex-col items-center justify-between text-center p-2 pb-4 pt-6 relative drop-shadow-addons cursor-pointer 
										supports-[hover:hover]:hover:outline-orange supports-[hover:hover]:hover:duration-75 supports-[hover:hover]:hover:scale-105
										outline-transparent border-background`}
								>
									{groupedItems[element.name] && groupedItems[element.name].length > 0 && (
										<div className="relative flex justify-center items-center h-10 mb-1">
											{groupedItems[element.name].slice(0, 3).map((item, index, arr) => (
												<div
													key={`thumb-${item.id || index}`}
													className="absolute size-12 rounded-lg overflow-hidden border border-2 border-white bg-white shadow-md"
													style={{
														zIndex:
															arr.length === 1 ? 2 :
																arr.length === 2 ? (index === 0 ? 3 : 2) :
																	// arr.length === 3 (or more, though we slice at 3)
																	(index === 1 ? 3 : 2),
														transform:
															arr.length === 1 ? 'translateY(0px) rotate(0deg)' :
																arr.length === 2 ? (
																	index === 0 ? 'translateX(-4px) translateY(1px) rotate(-6deg)' :
																		'translateX(4px) translateY(1px) rotate(6deg)'
																) :
																	// arr.length === 3 (or more)
																	(
																		index === 0 ? 'translateX(-6px) translateY(2px) rotate(-10deg)' :
																			index === 1 ? 'translateY(0px) rotate(0deg)' :
																				'translateX(6px) translateY(2px) rotate(10deg)'
																	),
													}}
												>
													<img
														src={item.imageUrl}
														alt={item.title}
														className="w-full h-full object-cover"
													/>
												</div>
											))}
										</div>
									)}
									<div className="flex flex-row items-center justify-center transition-transform group-hover:scale-105">
										{isExpanded ?
											<ChevronLeft size={18} className="" /> :
											<ChevronLeft size={18} className="opacity-0" />
										}
										<h4 className="text-sm font-headline m-0 max-w-full break-words">{element.name}</h4>
										{isExpanded ?
											<ChevronRight size={18} className="opacity-0" /> :
											<ChevronRight size={18} className="" />
										}
									</div>
								</button>
							</div>
						);
					} else if ('type' in element && element.type === 'moreButton') {
						const { items } = element as { type: 'moreButton'; items: IAddonItem[] };
						return (
							<div className="item flex flex-col gap-1 m-1 grow-0 max-w-[120px] transform-gpu" key="more-button">
								<button
									onClick={() => {
										if (onMoreButtonClick) {
											onMoreButtonClick(items);
										}
									}}
									className={`group group-header w-[120px] aspect-[4/4] transition-all ease-in-out border border-[3px] rounded-xl 
										overflow-hidden outline outline-[3px] md:hover:outline-orange hover:duration-75 md:hover:scale-105 bg-white 
										flex flex-col items-center justify-between text-center p-2 pb-4 pt-7 relative drop-shadow-addons cursor-pointer 
										supports-[hover:hover]:hover:outline-orange supports-[hover:hover]:hover:duration-75 supports-[hover:hover]:hover:scale-105
										outline-transparent border-background`}
								>
									{items && items.length > 0 && (
										<div className="relative flex justify-center items-center h-10 mb-3">
											{items.slice(0, 3).map((item, index, arr) => (
												<div
													key={`more-thumb-${item.id || index}`}
													className="absolute size-12 rounded-lg overflow-hidden border border-2 border-white bg-white shadow-md"
													style={{
														zIndex:
															arr.length === 1 ? 2 :
																arr.length === 2 ? (index === 0 ? 3 : 2) :
																	// arr.length === 3 (or more, though we slice at 3)
																	(index === 1 ? 3 : 2),
														transform:
															arr.length === 1 ? 'translateY(0px) rotate(0deg)' :
																arr.length === 2 ? (
																	index === 0 ? 'translateX(-4px) translateY(1px) rotate(-6deg)' :
																		'translateX(4px) translateY(1px) rotate(6deg)'
																) :
																	// arr.length === 3 (or more)
																	(
																		index === 0 ? 'translateX(-6px) translateY(2px) rotate(-10deg)' :
																			index === 1 ? 'translateY(0px) rotate(0deg)' :
																				'translateX(6px) translateY(2px) rotate(10deg)'
																	),
													}}
												>
													<img
														src={item.imageUrl}
														alt={item.title}
														className="w-full h-full object-cover"
													/>
												</div>
											))}
										</div>
									)}
									<div className="flex flex-row items-center justify-center transition-transform group-hover:scale-105">
										<Plus size={20} className="text-orange mr-0.5" />
										<h4 className="text-base font-headline mr-2 max-w-full break-words">More</h4>
									</div>
								</button>
							</div>
						);
					} else {
						const item = element as IAddonItem;
						const statusLabel = getStatusLabel(item.stockStatus);
						const isSharedPoolBlocked = item.sharedPoolBlocked === true
						const isDisabled = !isPurchasableStatus(item.stockStatus, item.availableForSale) || isSharedPoolBlocked;
						const stockMessage = formatStockMessage(item.stockMessage, item.remainingStock);
						return (
							<div className="item flex flex-col gap-3 m-1 grow-0 max-w-[120px] transform-gpu" key={item.id || `item-${Math.random()}`}>
								<button
									onClick={() => {
										if (isDisabled) {
											onBlockedSelect?.(item)
											return
										}
										if (item.triggersSpecialSelect && onSpecialSelect) {
											onSpecialSelect()
											onSelect(item.id)
										} else if (item.triggerInput) {
											setInputVisible(true)
											onSelect(item.id)
										} else {
											setInputVisible(false)
											onSelect(item.id)
										}
									}}
									aria-disabled={isDisabled}
									className={`product-thumbnail w-[110px] md:w-[120px] aspect-[4/4] transition-all ease-in-out border border-[3px] border-background rounded-xl
										overflow-hidden outline outline-[3px] md:hover:outline-orange hover:duration-75 md:hover:scale-105 bg-white relative
										drop-shadow-addons supports-[hover:hover]:hover:outline-orange supports-[hover:hover]:hover:duration-75 supports-[hover:hover]:hover:scale-105
										${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
										${item.id === activeId
											? 'outline-orange duration-100'
											: 'outline-transparent duration-300'
										}`}
								>
									{item.imageUrl === ''
										? <div className='w-full h-full flex items-center justify-center'><Ban className='h-8 w-8 text-grey' /></div>
										: <div className="absolute inset-0">
											<img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
											{isDisabled && (
												<div className="absolute inset-0 bg-black/60 flex items-center justify-center">
													<div className={`${isSharedPoolBlocked ? 'bg-amber-600' : 'bg-red-600'} text-white px-2 py-1 rounded text-xs font-bold text-center`}>
														{isSharedPoolBlocked ? 'LIMITED STOCK' : (statusLabel ? statusLabel.toUpperCase() : 'UNAVAILABLE')}
													</div>
												</div>
											)}
										</div>
									}
								</button>
								<div className="item-info relative flex flex-col grow-0 leading-none relative left-1">
									<h4 className={`relative m-0 max-w-full ${primaryTextColor || 'text-black'}`}>{item.title}</h4>
									{statusLabel && (
										<div
											className={`px-1 py-0.5 mt-0.5 rounded text-[10px] font-semibold w-fit ${item.stockStatus === 'PREORDER' ? 'bg-specialYellow text-black' : 'bg-gray-800 text-white'
												}`}
										>
											{statusLabel}
										</div>
									)}
									{stockMessage && (
										<div className="text-[11px] text-red-600 mt-0.5">{stockMessage}</div>
									)}
									{item.sharedPoolMessage && (
										<div className="text-[11px] text-amber-700 mt-0.5">{item.sharedPoolMessage}</div>
									)}
									{!item.sharedPoolMessage && item.stockStatus === 'LOW_STOCK' && typeof item.remainingStock === 'number' && (
										<div className="text-[11px] text-red-600 mt-0.5">Only {item.remainingStock} left</div>
									)}
									{
										item.isFree ? (
											<div className="bg-green text-white px-1 py-0.5 mt-0.5 rounded text-xs font-semibold w-fit">FREE</div>
										) : (
											renderPriceLabel(item)
										)}
								</div>
							</div>
						)
					}
				})}
			</div>

			<div className={`input flex flex-col gap-2 mt-2 overflow-visible transition-all duration-500 ease-hypereaseout ${inputVisible ? 'max-h-20 opacity-100 mb-2' : 'max-h-0 opacity-0 pointer-events-none'}`}>
				{inputVisible && (
					<div className="relative w-full">
						{activeId && renderInput(items.find(item => item.id === activeId) || defaultItem)}
					</div>
				)}
			</div>
		</div>
	)
}

export default Addon
